<template>
	<div class="deleted-students">
		<v-row>
			<v-col cols="12">
				<v-card class="mx-4 mb-4">
					<v-card-title>
						Deleted Student Classroom Records
						<v-spacer></v-spacer>
						<v-text-field
							v-model="search"
							append-icon="mdi-magnify"
							label="Search"
							single-line
							hide-details
						></v-text-field>
					</v-card-title>
					<v-row class="mx-4">
						<v-col cols="12" md="4">
							<v-select
								v-model="filter.room"
								:items="rooms"
								small-chips
								outlined
								color="primary"
								label="Filter by Classroom"
								multiple
								hide-details
								dense
								background-color="transparent"
								item-value="id"
								item-text="sections"
								class="mb-4 rounded-input"
								@change="getAllDeletedCurrentStudents()"
							></v-select>
						</v-col>
					</v-row>
					<v-data-table
						:headers="headers"
						:items="students"
						:search="search"
						:options.sync="options"
						:items-per-page="15"
						:multi-sort="true"
						:server-items-length="pagination.totalCount"
						class=""
					>
						<template v-slot:item.gr_no="{ item }">
							<h5>{{ item.student.gr_no || "-" }}</h5>
						</template>
						<template v-slot:item.image="{ item }">
							<v-avatar width="30" height="30" class="mb-2">
								<img :src="item.student.image" />
							</v-avatar>
						</template>
						<template v-slot:item.room="{ item }">
							<v-chip color="primary" outlined>
								<h5 v-if="item.room">{{ item.room.sections }}</h5>
								<h5 v-else>Not Assigned</h5>
							</v-chip>
						</template>
						<template v-slot:item.full_name="{ item }">
							<span>{{ item.student.full_name }}</span>
						</template>
						<!-- eslint-disable-next-line vue/no-unused-vars -->
						<template v-slot:item.actions="{ item }">
							<div>
								<v-tooltip :disabled="item.is_restorable" bottom>
									<template v-slot:activator="{ on }">
										<div v-on="on">
											<v-btn
												rounded
												outlined
												color="primary"
												:disabled="isLoading || !item.is_restorable"
												@click="triggerRestoreDialog(item.id)"
											>
												Restore
												<v-icon class="ml-2">
													mdi-backup-restore
												</v-icon>
											</v-btn>
										</div>
									</template>
									<span>Cannot restore this record. Classroom is already allocated to this student for the mentioned academic year.</span>
								</v-tooltip>
							</div>
						</template>
						<template v-slot:item.session="{ item }">
							<span>{{ currentInstitution.academic_sessions.find(session => session.id == item.session).title }}</span>
						</template>
						<template v-slot:item.deleted_by="{ item }">
							<span>{{ item.deleted_by ? item.deleted_by.full_name : "-" }}</span>
						</template>
					</v-data-table>
				</v-card>
			</v-col>
		</v-row>

		<DeleteConfirmationDialog
			:visible="showConfirmationDialog"
			title="Restore this deleted student ?"
			description="Are you sure you want to continue? Proceeding will restore this student's records."
			confirmationButtonText="Proceed"
			@deleteObject="restoreDeletedStudent"
		/>

		<v-snackbar v-model="snackbar.visible">
			{{ snackbar.text }}
			<template>
				<v-btn color="pink" text @click="snackbar.visible = false">Close</v-btn>
			</template>
		</v-snackbar>
	</div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import api from "@api/index";
import endpoints from "@api/repository";
import Helper from "@utils/misc";
import DeleteConfirmationDialog from "@components/widgets/dialogs/DeleteConfirmationDialog.vue";

export default {
	name: "DeletedStudents",
	components: {
		DeleteConfirmationDialog
	},
	data() {
		return {
			search: "",
			options: {},
			pagination: {
				totalCount: 0,
				url: endpoints.deletedCurrentStudentRecords,
			},
			snackbar: {
				visible: false,
				message: "",
				color: "dark",
			},

			headers: [
				{
					text: "GR No.",
					align: "center",
					sortable: false,
					value: "gr_no"
				},
				{ text: "Photo", value: "image", align: "center", sortable: false },
				{ text: "Name", align: "center", value: "full_name" },
				{ text: "Phone Number", align: "center", value: "student.phone", sortable: false },
				{ text: "Classroom", value: "room", align: "center" },
				{ text: "Academic Year", value: "session", align: "center" },
				{ text: "Deleted By", value: "deleted_by", align: "center", sortable: false },
				{ text: "Actions", value: "actions", align: "center", sortable: false },
			],

			students: [],

			rooms: [],

			filter: {
				category: null,
				room: null,
			},

			showConfirmationDialog: false,
			selectedStudentId: null
		};
	},

	computed: {
		...mapGetters(["accessToken", "currentUser", "isLoading", "currentInstitution"]),
		apiEssentials() {
			return {
				accessToken: this.accessToken,
				handleErrorFunction: this.handleApiErrors,
				setLoading: this.setLoading,
			};
		},
	},

	methods: {
		...mapActions(["setLoading"]),

		// handles api errors
		async handleApiErrors(err) {
			this.setLoading(false);
			if (err.response) {
				console.log(err.response);
			} else {
				this.snackbar.text = "Unknown error occurred";
				this.snackbar.visible = true;
			}
		},

		// fetch all deleted students
		async getAllDeletedCurrentStudents() {
			var url = this.pagination.url;
			// if search query exists, add to the url as a GET param w/o disturbing other params
			if (this.search) {
				url += (url.split("?")[1] ? "&" : "?") + "search=" + this.search;
			}
			if (this.filter.room) {
				url += "&room__in=" + this.filter.room;
			}
			var response = await api.call(this.apiEssentials, url)

			if (response) {
				this.students = response.results;
				this.pagination.totalCount = response.count;
				this.pagination.nextUrl = response.next;
			}
		},

		// quries rooms
		async getRooms() {
			var response
			var url = endpoints.getRooms

			response = await api.handleApi({
				apiEssentials: this.apiEssentials,
				url: url,
				responseVar: response,
			})

			if (response instanceof Error) {
				this.setLoading(false)
				this.snackbar.text = "Error retrieving rooms"
				this.snackbar.visible = true
				return
			}
			this.rooms = response
		},

		triggerRestoreDialog(id) {
			this.selectedStudentId = id
			this.showConfirmationDialog = true
		},

		// restore a deleted student
		async restoreDeletedStudent(confirm) {
			if(confirm) {
				var url = endpoints.deletedCurrentStudentRecords + `${this.selectedStudentId}/restore/`
				// var post_body = { 'soft_deleted' : false }
				var response = await api.call(this.apiEssentials, url, api.Methods.POST)

				if(response){
					this.snackbar.text = "Successfully restored the deleted student!"
					this.snackbar.visible = true
					this.getAllDeletedCurrentStudents()
					this.showConfirmationDialog = false
					this.selectedStudentId = null
				}
				else{
					this.snackbar.text = "Cannot Restore the student. Contact Support!"
					this.snackbar.visible = true
				}
			}
			else
				this.showConfirmationDialog = false
		},
	},

	watch: {
		options: {
			async handler() {
				this.pagination.url = 
				endpoints.deletedCurrentStudentRecords + "?page=" + this.options.page;
				if (this.options.sortBy.length > 0) {
					// get url var
					var params = "&ordering=";
					// iterate over sortBy options and add sortby and sortdesc seperated with commas
					for (var sortOption in this.options.sortBy) {
						params +=
						(this.options.sortDesc[sortOption] ? "" : "-") +
						this.options.sortBy[sortOption];
						if (sortOption != this.options.sortBy.length - 1) params += ",";
					}

					params = Helper.replaceTextFromArray(params, [
						["full_name", "student__full_name"],
						["room", "room__first_section__order"],
					]);
					
					this.pagination.url += params;
				}
				this.getAllDeletedCurrentStudents();
			},
		},
		search: {
			async handler() {
				this.options.page = 1;
				var searched = this.search;
				var self = this;
				setTimeout(function () {
					if (searched == self.search) {
						self.getAllDeletedCurrentStudents();
					}
				}, 500);
			},
		},
	},

	created() {
		this.getAllDeletedCurrentStudents()
		this.getRooms()
	},
};
</script>
